import { useEffect, useMemo, useState } from "react";
import {
  X,
  Info,
  CreditCard,
  ChevronLeft,
  ChevronRight,
  NotebookTabs,
  HelpCircle,
  Cpu,
  Server,
} from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { APP_META } from "@/lib/utils";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import type { LLMSettings, MCPConfig } from "@/types";
import { defaultMCPConfig, saveMCPConfig, loadMCPConfig } from "@/services/settings";

// Panel tab components
import { AccountTab } from "./panel-tabs/AccountTab";
import { LLMTab } from "./panel-tabs/LLMTab";
import { MCPServersTab } from "./panel-tabs/MCPServersTab";
import { RegistriesTab } from "./panel-tabs/RegistriesTab";
import { AboutTab } from "./panel-tabs/AboutTab";

export type TabKey = "account" | "security" | "appearance" | "notifications" | "about" | "registries" | "llm" | "mcpservers";

const tabs: { key: TabKey; label: string; icon: React.ReactNode }[] = [
  { key: "account", label: "Account", icon: <CreditCard className="h-5 w-5" /> },
  { key: "llm", label: "LLM", icon: <Cpu className="h-5 w-5" /> },
  { key: "mcpservers", label: "MCP Servers", icon: <Server className="h-5 w-5" /> },
  // { key: "security", label: "Security", icon: <Shield className="h-5 w-5" /> },
  { key: "registries", label: "MCP Registries", icon: <NotebookTabs className="h-5 w-5" /> },
  // { key: "appearance", label: "Appearance", icon: <Palette className="h-5 w-5" /> },
  // { key: "notifications", label: "Notifications", icon: <Bell className="h-5 w-5" /> },
  { key: "about", label: "About", icon: <Info className="h-5 w-5" /> },
];

interface Registry {
  id: string;
  name: string;
  description: string;
  url: string;
  isEditing?: boolean;
  draft?: Pick<Registry, "name" | "description" | "url">;
}

interface AccountSettingsPanelProps {
  onClose: () => void;
  defaultTab?: TabKey;
  llmSettings: LLMSettings;
  onLlmSettingsChange: (settings: LLMSettings) => void;
}

export const AccountSettingsPanel = ({
  onClose,
  defaultTab = "account",
  llmSettings,
  onLlmSettingsChange,
}: AccountSettingsPanelProps) => {
  const [activeTab, setActiveTab] = useState<TabKey>(defaultTab);
  const [isCollapsed, setIsCollapsed] = useState(false);

  // State for each tab (move to respective tab if desired, but keep here for now for minimal change)
  const [registries, setRegistries] = useState<Registry[]>([
    { id: "mcp-ai", name: "HAPI MCP Registry", description: "HAPI MCP tools based on API-First principles, powered by HAPI Stack.", url: "https://registry.mcp.com.ai" },
    { id: "official", name: "Official MCP Registry", description: "Discover Model Context Protocol servers.", url: "https://registry.modelcontextprotocol.io" },
    { id: "quetzalcoatl", name: "Ketzal Registry", description: "Shared MCP tools and general knowledge.", url: "https://ketza.registry.mcp.com.ai" },
    { id: "huitzilopochtli", name: "Weetzee Registry", description: "MCP tools focused on security and privacy.", url: "https://weetzee.registry.mcp.com.ai" },
    { id: "itzcuintli", name: "Itzy Registry", description: "Infra/DevOps oriented MCP tools.", url: "https://itzy.registry.mcp.com.ai" },    
  ]);
  const [newRegistry, setNewRegistry] = useState({ name: "", description: "", url: "" });
  const [llmDraft, setLlmDraft] = useState<LLMSettings>(llmSettings);
  const [mcpConfig, setMcpConfig] = useState<MCPConfig>(loadMCPConfig());

  useEffect(() => {
    const handler = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [onClose]);

  useEffect(() => {
    setLlmDraft(llmSettings);
  }, [llmSettings]);

  const canAdd = useMemo(() => newRegistry.name.trim().length > 0 && newRegistry.url.trim().length > 0, [newRegistry]);
  const updateLlm = <K extends keyof LLMSettings>(key: K, value: LLMSettings[K]) =>
    setLlmDraft(prev => ({ ...prev, [key]: value }));

  // Refactored: Use subcomponents for each tab
  const renderTabContent = () => {
    switch (activeTab) {
      case "account":
        return <AccountTab />;
      case "llm":
        return (
          <LLMTab
            llmDraft={llmDraft}
            setLlmDraft={setLlmDraft}
            llmSettings={llmSettings}
            onLlmSettingsChange={onLlmSettingsChange}
            toast={toast}
          />
        );
      case "mcpservers":
        return (
          <MCPServersTab
            mcpConfig={mcpConfig}
            setMcpConfig={setMcpConfig}
            defaultMCPConfig={defaultMCPConfig}
            saveMCPConfig={saveMCPConfig}
            toast={toast}
          />
        );
      case "registries":
        return (
          <RegistriesTab
            registries={registries}
            setRegistries={setRegistries}
            newRegistry={newRegistry}
            setNewRegistry={setNewRegistry}
            canAdd={canAdd}
          />
        );
      case "about":
        return <AboutTab APP_META={APP_META} Card={Card} CardHeader={CardHeader} CardTitle={CardTitle} CardContent={CardContent} HelpCircle={HelpCircle} />;
      case "appearance":
        return <p className="text-xs sm:text-sm text-muted-foreground">Appearance controls will live here.</p>;
      case "notifications":
        return <p className="text-xs sm:text-sm text-muted-foreground">Notification preferences will live here.</p>;
      case "security":
        return <p className="text-xs sm:text-sm text-muted-foreground">Security settings will live here.</p>;
      default:
        return null;
    }
  };

  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/30 p-2 sm:p-4 backdrop-blur-sm overflow-y-auto">
      <div className="w-full max-w-4xl my-auto rounded-xl sm:rounded-2xl border border-white/60 bg-white/95 shadow-2xl dark:bg-slate-900 max-h-[95vh] flex flex-col">
        {/* Header */}
        <div className="flex items-start justify-between gap-2 sm:gap-3 border-b border-border/60 p-3 sm:p-4 md:p-6 flex-shrink-0">
          <div className="space-y-0.5 sm:space-y-1 min-w-0 flex-1">
            <p className="text-[10px] sm:text-xs uppercase tracking-wide text-primary">Settings</p>
            <h3 className="text-base sm:text-xl font-semibold text-foreground">Account settings</h3>
            <p className="text-xs sm:text-sm text-muted-foreground">
              Manage your account<span className="hidden sm:inline">, appearance, and notifications</span>.
            </p>
          </div>
          <Button variant="ghost" size="icon" onClick={onClose} aria-label="Close settings" className="flex-shrink-0 h-8 w-8 sm:h-10 sm:w-10">
            <X className="h-4 w-4 sm:h-5 sm:w-5" />
          </Button>
        </div>

        {/* Main Content Area with Vertical Tabs */}
        <div className="flex flex-1 min-h-0 overflow-hidden">
          {/* Vertical Tabs Sidebar - Icon-only on mobile, expandable on desktop */}
          <div
            className={`flex flex-col border-r border-border/60 bg-white/70 transition-all duration-300 dark:bg-white/5 flex-shrink-0 w-12 ${isCollapsed ? "sm:w-16" : "sm:w-48"
              }`}
            role="tablist"
            aria-orientation="vertical"
          >
            {/* Collapse/Expand Button - Hidden on mobile */}
            <div className="hidden sm:flex h-10 sm:h-12 items-center justify-end border-b border-border/40 px-1 sm:px-2">
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setIsCollapsed(!isCollapsed)}
                aria-label={isCollapsed ? "Expand sidebar" : "Collapse sidebar"}
                className="h-7 w-7 sm:h-8 sm:w-8"
              >
                {isCollapsed ? <ChevronRight className="h-3.5 w-3.5 sm:h-4 sm:w-4" /> : <ChevronLeft className="h-3.5 w-3.5 sm:h-4 sm:w-4" />}
              </Button>
            </div>

            {/* Tab Buttons */}
            <div className="flex flex-col gap-0.5 sm:gap-1 p-1 sm:p-2 overflow-y-auto">
              {tabs.map(tab => (
                <button
                  key={tab.key}
                  type="button"
                  role="tab"
                  aria-selected={activeTab === tab.key}
                  aria-controls={`panel-${tab.key}`}
                  onClick={() => setActiveTab(tab.key)}
                  className={`flex items-center gap-2 sm:gap-3 rounded-lg px-2 py-2 sm:px-3 sm:py-3 text-xs sm:text-sm font-medium transition-all justify-center ${activeTab === tab.key
                      ? "bg-primary text-primary-foreground shadow-sm"
                      : "text-foreground hover:bg-muted"
                    } ${isCollapsed ? "sm:justify-center" : "sm:justify-start"}`}
                  title={tab.label}
                >
                  <span className="flex-shrink-0 [&>svg]:h-4 [&>svg]:w-4 sm:[&>svg]:h-5 sm:[&>svg]:w-5">{tab.icon}</span>
                  <span
                    className={`whitespace-nowrap transition-all text-[10px] sm:text-sm hidden ${isCollapsed ? "sm:hidden" : "sm:inline"
                      }`}
                  >
                    {tab.label}
                  </span>
                </button>
              ))}
            </div>
          </div>

          {/* Content Area */}
          <div className="flex-1 p-3 sm:p-4 md:p-6 overflow-y-auto min-w-0">
            <div
              role="tabpanel"
              id={`panel-${activeTab}`}
              aria-labelledby={`tab-${activeTab}`}
              className="min-h-0"
            >
              {renderTabContent()}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
